import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import type { ToastMessage } from '@/state/types';

const ICONS: Record<ToastMessage['kind'], LucideIcon> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info,
  error: XCircle,
};

const TONE: Record<ToastMessage['kind'], string> = {
  success: 'border-status-available/50 text-status-available',
  warning: 'border-status-enroute/50 text-status-enroute',
  info: 'border-accent-dispatch/50 text-accent-dispatch',
  error: 'border-accent-emergency/50 text-accent-emergency',
};

const AUTO_DISMISS_MS = 4500;
const MAX_VISIBLE = 3;

export function ToastLayer() {
  const toasts = useGameStore((s) => s.toasts);
  const dismissToast = useGameStore((s) => s.dismissToast);
  const viewport = useViewport();

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) =>
      setTimeout(() => dismissToast(t.id), AUTO_DISMISS_MS - (Date.now() - t.createdAt)),
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismissToast]);

  // Show the most recent N — older ones are summarised in a single chip.
  const visible = toasts.slice(-MAX_VISIBLE);
  const overflow = toasts.length - visible.length;

  // Mobile: bottom-center stack; Desktop/Tablet: top-right.
  const positionClass =
    viewport === 'mobile'
      ? 'pointer-events-none fixed bottom-32 left-1/2 z-40 flex w-[90vw] max-w-md -translate-x-1/2 flex-col gap-2'
      : 'pointer-events-none fixed right-4 top-20 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2';

  return (
    <div className={positionClass}>
      {overflow > 0 && (
        <div
          className="pointer-events-auto flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-bg-overlay/90 px-3 py-1.5 text-xs text-fg-muted shadow-lg backdrop-blur-md"
          aria-label={`${overflow} weitere Meldungen`}
        >
          <span>+{overflow} weitere Meldung{overflow === 1 ? '' : 'en'}</span>
          <button
            type="button"
            onClick={() => {
              for (const t of toasts.slice(0, overflow)) dismissToast(t.id);
            }}
            aria-label="Alle ausblenden"
            className="text-fg-muted hover:text-fg-primary"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </div>
      )}

      <AnimatePresence initial={false}>
        {visible.map((t) => {
          const Icon = ICONS[t.kind];
          return (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, scale: 0.95 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className={[
                'pointer-events-auto flex items-start gap-2.5 rounded-md border bg-bg-overlay px-3 py-2.5 shadow-lg backdrop-blur-md',
                TONE[t.kind],
              ].join(' ')}
            >
              <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
              <p className="flex-1 text-sm text-fg-primary">{t.text}</p>
              <button
                type="button"
                onClick={() => dismissToast(t.id)}
                aria-label="Schließen"
                className="text-fg-muted transition-colors hover:text-fg-primary"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
